import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';

import { env } from '@amfam/environment';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';

import { loadSuccess } from '../../core/store/error/error.actions';

@Injectable()
export class ApplicationErrorHandler extends ErrorHandler {
  private injector = inject(Injector);

  handleError(error: unknown) {
    const stack = error instanceof Error ? (error.stack ?? '') : '';

    // Third-party script errors never indicate app failure — dropping them prevents false-positive error-page redirects.
    if (stack.includes('chrome-extension://') || stack.includes('googletagmanager.com')) {
      return;
    }
    // Cross-origin iframe SecurityErrors are expected from third-party widgets
    // (e.g. Salesforce Embedded Messaging). They are harmless and must not
    // trigger the error page redirect.
    if (error instanceof DOMException && error.name === 'SecurityError') {
      super.handleError(error);
      return true;
    }

    // Network/CORS errors (status 0) are transient — do not redirect to /error
    // and do not dispatch into NgRx (devtools history grows unbounded under flood).
    if (error instanceof HttpErrorResponse && error.status === 0) {
      super.handleError(error);
      return true;
    }

    try {
      // Lazy inject to avoid circular dependency
      const store = this.injector.get(Store);
      const route = this.injector.get(Location);

      if (route.path().indexOf('error') < 0) {
        const dataDogService = this.injector.get(DataDogService);
        dataDogService.log(
          `Unhandled error caught: ${error instanceof Error ? error.message : String(error)}`,
          'error',
          {
            currentRoute: route.path(),
            errorName: error instanceof Error ? error.name : undefined,
            errorStack: error instanceof Error ? error.stack : undefined
          }
        );
      }

      if (!env.suppressErrorPage) {
        if (error instanceof Error) {
          store.dispatch(loadSuccess({ message: error.message, stack: error.stack }));
        }
        if (route.path().indexOf('error') < 0) {
          store.dispatch(
            fromRouterActions.Go({
              path: ['/error']
            })
          );
        }
      }
    } catch (e) {
      try {
        const dataDogService = this.injector.get(DataDogService);
        dataDogService.log('Error handler caused error', 'error', {
          error: e instanceof Error ? e.message : String(e),
          originalError: error instanceof Error ? error.message : String(error)
        });
      } catch (e1) {
        if (!env.production) {
          console.error('ApplicationErrorHandler: DataDogService unavailable', e1);
        } else {
          console.error('An error occurred in the error handler.');
        }
      }
    }
    super.handleError(error);
    return true;
  }
}
